<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="d-flex align-items-center justify-content-between">
                            <p class="mb-0">Editar Usuario: {{ user.full_name }}</p>

                            <argon-button
                                color="warning"
                                size="sm"
                                @click="requestPasswordReset"
                            >
                                Reestablecer Contraseña
                            </argon-button>
                        </div>
                    </div>

                    <div class="card-body">
                        <div v-if="isLoading" class="text-center py-5">
                            Cargando datos del usuario...
                        </div>

                        <form v-else @submit.prevent="submitUser">
                            <div class="row">
                                <div class="col-md-6">
                                    <label class="form-control-label">Nombre Completo</label>
                                    <argon-input v-model="user.full_name" type="text" placeholder="Juan Pérez" />
                                    <small v-if="submitted && v$.user.full_name.$error" class="text-danger">Nombre completo requerido.</small>
                                </div>

                                <div class="col-md-6">
                                    <label class="form-control-label">Numero de Documento</label>
                                    <argon-input v-model="user.document" type="text" placeholder="Juan Pérez" />
                                    <small v-if="submitted && v$.user.document.$error" class="text-danger">Numero de documento es requerido.</small>
                                </div>

                                <div class="col-md-6">
                                    <label class="form-control-label">Username</label>
                                    <argon-input v-model="user.username" type="text" placeholder="juan.perez" readonly disabled />
                                    </div>

                                <div class="col-md-6">
                                    <label class="form-control-label">Email</label>
                                    <argon-input v-model="user.email" type="email" placeholder="email@dominio.com" />
                                    <small v-if="submitted && v$.user.email.$error" class="text-danger">Email requerido y válido.</small>
                                </div>

                                <div class="col-md-6">
                                    <label class="form-control-label">Rol</label>
                                    <select class="form-control" v-model.number="user.role_id">
                                        <option :value="null">Seleccione un rol</option>
                                        <option v-for="role in roles" :key="role.id" :value="role.id">
                                            {{ role.name }}
                                        </option>
                                    </select>
                                    <small v-if="submitted && v$.user.role_id.$error" class="text-danger">Rol requerido.</small>
                                </div>

                                <div class="col-md-6 mt-3">
                                    <label class="form-control-label">Estado</label>
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" id="isActiveSwitch" v-model="user.is_active" :true-value="1" :false-value="0">
                                        <label class="form-check-label" for="isActiveSwitch">{{ user.is_active === 1 ? 'Activo' : 'Inactivo' }}</label>
                                    </div>
                                </div>
                            </div>

                            <argon-button
                                type="submit"
                                class="mt-4"
                                color="success"
                                fullWidth
                                size="lg"
                            >
                                Guardar Cambios
                            </argon-button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useVuelidate } from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import axios from "axios";
import Swal from 'sweetalert2';

export default {
    name: "EditUser",
    components: { ArgonInput, ArgonButton },
    setup: () => ({ v$: useVuelidate() }),
    data() {
        return {
            isLoading: true,
            submitted: false,
            roles: [],
            user: {
                id: this.$route.params.id,
                username: '',
                email: '',
                role_id: null,
                full_name: '',
                document: '',
                is_active: 1,
            }
        };
    },
    validations() {
        return {
            user: {
                full_name: { required: helpers.withMessage('Requerido', required) },
                document: { required: helpers.withMessage('Requerido', required) },
                username: { required: helpers.withMessage('Requerido', required) }, // Aunque es readonly, mantenemos validación por si se cambia el campo
                email: {
                    required: helpers.withMessage('Requerido', required),
                    email: helpers.withMessage('Email inválido', email)
                },
                role_id: { required: helpers.withMessage('Rol requerido', required) },
                is_active: { required: helpers.withMessage('Estado requerido', required) },
            }
        };
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.isLoading = true;
            const token = localStorage.getItem("accessToken");
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            const userId = this.user.id;

            try {
                // 1. Cargar Roles
                const rolesResponse = await axios.get(this.$store.state.apiURL + 'roles', config);
                this.roles = rolesResponse.data;

                // 2. Cargar datos del usuario
                const userResponse = await axios.get(this.$store.state.apiURL + 'users/' + userId, config);
                this.user = {
                    ...this.user,
                    ...userResponse.data,
                    // Asegura que el estado sea un número para el checkbox
                    is_active: parseInt(userResponse.data.is_active)
                };

            } catch (error) {
                console.error("Error al cargar datos:", error.response || error);
                this.handleSessionError(error);
            } finally {
                this.isLoading = false;
            }
        },

        async submitUser() {
            this.submitted = true;
            this.v$.$touch();

            if (this.v$.$invalid) {
                Swal.fire({ title: 'Datos inválidos', text: 'Por favor, revisa los campos requeridos.', icon: 'warning', confirmButtonColor: '#0b9257' });
                return;
            }

            const token = localStorage.getItem("accessToken");
            const apiURL = this.$store.state.apiURL;
            const userId = this.user.id;

            const reqConfig = { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } };

            // 🛑 Payload de edición: elimina el campo 'password' y cualquier campo de token 🛑
            let payload = { ...this.user };
            delete payload.password;
            delete payload.password_hash;
            delete payload.reset_token;
            delete payload.token_expiration;

            try {
                // PUT para edición
                await axios.put(`${apiURL}users/${userId}`, payload, reqConfig);

                Swal.fire({
                    title: 'Actualizado',
                    text: `El usuario ${this.user.username} fue actualizado correctamente.`,
                    icon: 'success',
                    confirmButtonColor: '#0b9257'
                }).then(() => {
                    this.$router.push('/settings/users');
                });

            } catch (err) {
                console.error('Error al guardar usuario:', err.response || err);
                const errorText = (err.response && err.response.data && err.response.data.message)
                                 ? err.response.data.message : 'No se pudo guardar el usuario.';
                Swal.fire({ title: 'Error', text: errorText, icon: 'error', confirmButtonColor: '#0b9257' });
            }
        },

        async requestPasswordReset() {
            if (!this.user.id || !this.user.email) return;

            Swal.fire({
                title: 'Confirmar Restablecimiento',
                text: `¿Estás seguro de que deseas enviar un enlace de restablecimiento de contraseña al correo ${this.user.email}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#5e72e4',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, enviar enlace'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const token = localStorage.getItem("accessToken");
                    const apiURL = this.$store.state.apiURL;
                    const userId = this.user.id;
                    const reqConfig = { headers: { 'Authorization': `Bearer ${token}` } };

                    try {
                        await axios.post(`${apiURL}users/${userId}/request-reset`, {}, reqConfig);

                        Swal.fire('Enviado', `El enlace de restablecimiento ha sido enviado a ${this.user.email}.`, 'success');

                    } catch (err) {
                        console.error('Error al solicitar restablecimiento:', err.response || err);
                        const errorText = (err.response && err.response.data && err.response.data.message)
                                         ? err.response.data.message : 'No se pudo enviar el enlace. Verifique que el email sea válido.';
                        Swal.fire('Error', errorText, 'error');
                    }
                }
            });
        },

        // Manejo de error de sesión (mantenido)
        handleSessionError(error) {
            const self = this;
            if (error.response && error.response.status === 401) {
                Swal.fire({
                    title: 'Sesión',
                    text: 'El tiempo de sesión ha vencido, ingresa nuevamente.',
                    icon:'warning',
                    confirmButtonColor:'#bf4f51',
                }).then(function(){
                    localStorage.removeItem('user');
                    localStorage.removeItem('accessToken');
                    self.$router.push('/signin');
                });
            }
        }
    },
};
</script>